import React, { useState } from 'react';
import Styled from 'styled';
import { useQuery } from '@apollo/client/react/hooks';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import classnames from 'classnames';

import SVGIcon from 'lib/images/svg-icon/svg-icon';
import btoa from 'lib/base64/btoa';
import atob from 'lib/base64/atob';

import iconLike from 'four-nets/icons/svg/icon_like-3.svg';
import Avatar from 'four-nets/avatar/avatar';
import TextShortener from 'four-nets/ui-v2/text-shortener/text-shortener';

import ArrowWidgetButton from '../arrow-widget-button';

import query, { VendorWidgetQuery } from './vendor-widget.graphql';
import defaultStyles from './vendor-widget.scss';

interface Props {
    styles: typeof defaultStyles;
    categoryIds: string[];
}

const VendorWidget: React.FC<Props> = props => {
    const { styles, categoryIds } = props;

    const [after, setAfter] = useState<null | string>(null);

    const { data, loading, error } = useQuery<VendorWidgetQuery>(query, {
        variables: { catalogCategoryIds: categoryIds, first: 2, after },
        fetchPolicy: 'cache-first',
    });

    if (error || !data || !data.vendors || !data.vendors.edges) {
        return null;
    }

    const endCursor = data && data.vendors && data.vendors.pageInfo.endCursor;
    const startCursor = data && data.vendors && data.vendors.pageInfo.startCursor;

    const startIndex = (startCursor && parseInt(atob(startCursor).split(':')[1], 10)) || 0;
    const hasPreviousPage = startIndex > 0;
    const hasNextPage = data && data.vendors && data.vendors.pageInfo.hasNextPage;

    const prevPageAfter = startIndex < 3 ? null : btoa(`arrayconnection:${startIndex - 3}`);

    return (
        <div className={styles.widget}>
            <div className={styles.widgetTitle}>
                <FormattedMessage id='PB_MENU_VENDORS' />
            </div>
            <div className={styles.images}>
                <ArrowWidgetButton
                    onlyDesktop={true}
                    onClick={() => {
                        setAfter(prevPageAfter || null);
                    }}
                    direction='left'
                    hidden={!hasPreviousPage}
                />
                {data.vendors.edges.map(edge =>
                    edge && edge.node ? (
                        <div className={styles.imageWrapper}>
                            <div className={styles.image}>
                                <div className={styles.likes}>
                                    <SVGIcon style={{ image: styles.likeIcon }} src={iconLike} />
                                    {edge.node.photoblog && edge.node.photoblog.likesCount}
                                </div>
                                <Avatar style={{ avatar: styles.avatar }} user={edge.node.user} />
                            </div>
                            <div className={styles.vendorInfo}>
                                <Link to={edge.node.href} className={styles.vendorName}>
                                    <TextShortener
                                        text={edge.node.name || (edge.node.user && edge.node.user.username)}
                                        textLength={20}
                                    />
                                </Link>
                                {edge.node.category ? (
                                    <div className={styles.vendorCategory}>{edge.node.category.name}</div>
                                ) : null}
                                <TextShortener text={edge?.node?.category?.name || ''} textLength={30}>
                                    {text => (
                                        <div
                                            className={classnames(styles.vendorCardText, {
                                                [styles.ellipsis]: (edge?.node?.cardText || '') !== text,
                                            })}
                                        >
                                            {edge?.node?.cardText || ''}
                                        </div>
                                    )}
                                </TextShortener>
                                {edge.node.county ? (
                                    <div className={styles.vendorCity}>{edge?.node?.county?.name}</div>
                                ) : null}
                            </div>
                        </div>
                    ) : null
                )}
                <ArrowWidgetButton
                    onlyDesktop={true}
                    onClick={() => {
                        setAfter(endCursor || null);
                    }}
                    direction='right'
                    hidden={!hasNextPage}
                />
            </div>
        </div>
    );
};

export default Styled(defaultStyles)(VendorWidget);
